// ProviderDetailDialog.js
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Paper,
  Tooltip,
  Chip,
} from "@mhome/ui";
import { useMediaQuery, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DisconnectIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import Markdown from "../common/markdown";
import { getIntegrationModel } from "../service/integration-service";
import { useWebSocket } from "../ws/websocket";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

export default function ProviderDetailDialog({
  open,
  provider,
  onClose,
  onDisconnect,
  onNewConnect,
}) {
  const getComponentBgColor = useComponentBackgroundColor();
  const history = useHistory();
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [integrationModel, setIntegrationModel] = useState(null);
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const { sendAndWaitWs } = useWebSocket();
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");

  // Fetch integration model when dialog opens
  useEffect(() => {
    if (open && provider && provider.integration) {
      setIsLoadingModel(true);
      setIntegrationModel(null);

      getIntegrationModel(sendAndWaitWs, provider.integration)
        .then((response) => {
          if (response && response.integration) {
            setIntegrationModel(response.integration);
          }
        })
        .catch((error) => {
          console.error("Failed to load integration model:", error);
        })
        .finally(() => {
          setIsLoadingModel(false);
        });
    }
  }, [open, provider]);

  if (!provider) {
    return null;
  }

  console.debug("provider", provider);

  const handleDisconnectClick = (connection = null) => {
    setSelectedConnection(connection);
    setDisconnectConfirmOpen(true);
  };

  const confirmDisconnect = () => {
    setIsDisconnecting(true);

    // Call the actual disconnect function
    if (selectedConnection) {
      onDisconnect(provider, selectedConnection.id);
    } else {
      onDisconnect(provider);
    }

    setIsDisconnecting(false);
    setDisconnectConfirmOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isSmallScreen}
        variant={isSmallScreen ? undefined : "drawer"}
        PaperProps={{
          style: {
            width: isSmallScreen ? "100%" : "600px",
            marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
            marginBottom: isSmallScreen
              ? 0
              : `${safeAreaInsets.bottom}px !important`,
            height: isSmallScreen
              ? "100vh"
              : `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
            maxHeight: isSmallScreen
              ? "100vh"
              : `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
            paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
            paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
            paddingRight: `${safeAreaInsets.right}px`,
          },
        }}
      >
        <DialogTitle className="p-6 flex items-center gap-4 min-h-[48px]">
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle1" component="div" className="flex-1">
            Provider Details
          </Typography>
        </DialogTitle>

        <DialogContent className="px-4 pb-4 flex flex-col gap-6">
          <Paper
            variant="outlined"
            style={{
              padding: "16px",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <Typography variant="h6">{provider.name}</Typography>
            {/* Combined Chips Row */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {provider.hub && (
                <Tooltip
                  title="This integration uses local network communication, which requires a local Hub device. Click to set up a hub."
                  arrow
                >
                  <Chip
                    label="Hub Required"
                    size="small"
                    color="warning"
                    icon={<SettingsIcon style={{ fontSize: "0.875rem" }} />}
                    onClick={() => history.push("/hub/setting")}
                    clickable
                    style={{
                      fontSize: "0.75rem",
                      height: 24,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              )}
              {provider.multiConnect ? (
                <Tooltip
                  title="This integration supports multiple connections."
                  arrow
                >
                  <Chip
                    label="Multi-Conn"
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{
                      fontSize: "0.75rem",
                      height: 24,
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title="This provider supports only one connection at a time."
                  arrow
                >
                  <Chip
                    label="Unique-Conn"
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{
                      fontSize: "0.75rem",
                      height: 24,
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </Paper>

          {/* Connections Section */}
          <Paper
            variant="outlined"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <div
              className="flex items-center justify-between mx-4 py-2 border-b"
              style={{
                borderColor: "hsl(var(--border))",
              }}
            >
              <Typography variant="subtitle2">
                {provider.multiConnect ? "Active Connections" : "Connection"}
              </Typography>
              {provider.multiConnect ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onNewConnect(provider)}
                  className="normal-case"
                  style={{ boxShadow: "none" }}
                >
                  New Connection
                </Button>
              ) : !provider.installed ? (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={() => onNewConnect(provider)}
                  startIcon={<AddIcon />}
                  className="normal-case"
                  style={{ boxShadow: "none" }}
                >
                  Connect
                </Button>
              ) : null}
            </div>

            {provider.connects.length > 0 ? (
              <div className="flex flex-col">
                {provider.connects.map((connection, index) => (
                  <div
                    key={connection.id}
                    className="px-4 py-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Typography variant="body1">{connection.name}</Typography>
                      <Chip
                        label={
                          connection.status === "active" ? "active" : "inactive"
                        }
                        variant="outlined"
                        size="small"
                        color={
                          connection.status === "active" ? "success" : "error"
                        }
                        style={{
                          height: 20,
                        }}
                        className="px-2 text-xs"
                      />
                    </div>
                    <IconButton
                      aria-label="disconnect"
                      onClick={() => handleDisconnectClick(connection)}
                      style={{ color: "hsl(var(--destructive))" }}
                      size="small"
                    >
                      <DisconnectIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            ) : !provider.multiConnect && provider.installed ? (
              <div className="p-4">
                <Button
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={() => handleDisconnectClick()}
                  startIcon={<DisconnectIcon />}
                  style={{ textTransform: "none", boxShadow: "none" }}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="p-4">
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="italic"
                >
                  No active connections
                </Typography>
              </div>
            )}
          </Paper>

          {/* Description Section */}
          <Paper
            variant="outlined"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <div
              className="mx-4 py-2 border-b"
              style={{
                borderColor: "hsl(var(--border))",
              }}
            >
              <Typography variant="subtitle2">Description</Typography>
            </div>
            <div
              className="p-4"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              <Markdown>
                {integrationModel?.description ||
                  provider.aboutConnect ||
                  "No description available"}
              </Markdown>
            </div>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectConfirmOpen}
        onClose={() => setDisconnectConfirmOpen(false)}
        variant="pops"
      >
        <DialogTitle className="p-6 flex items-center gap-4 min-h-[48px]">
          <Typography variant="subtitle1">Confirm Disconnect</Typography>
        </DialogTitle>
        <DialogContent className="p-4 mt-2">
          <Typography variant="body2">
            Are you sure you want to disconnect{" "}
            {selectedConnection ? `this connection?` : `this provider?`}
          </Typography>
        </DialogContent>
        <DialogActions className="justify-between px-4 pb-4">
          <Button
            onClick={() => setDisconnectConfirmOpen(false)}
            size="small"
            variant="outlined"
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDisconnect}
            color="error"
            variant="contained"
            disabled={isDisconnecting}
            size="small"
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Disconnect
            {isDisconnecting && <CircularProgress size={16} className="ml-2" />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
